import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import { BLOCK_REASON_CODES } from "./constants";
import { createBlock, updateBlock } from "../../api/Scheduling/blocks";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { instantRangeToLocal, zonedRangeToInstants } from "./time";
import DateTimeRangeFields from "./DateTimeRangeFields";
import BlockOverlapWarning, { blockOverlapFromError } from "./BlockOverlapWarning";

const CalendarBlockForm = ({
  show,
  onHide,
  organizationRef,
  schedule,
  block,
  onSaved,
  initialRange,
  timezoneName: displayTimeZone,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const pending = useRef(false);
  const isEdit = !!block?.blockId;
  const timezoneName = displayTimeZone || schedule?.timezoneName || "UTC";

  const formik = useFormik({
    initialValues: {
      localDate: "",
      endDate: "",
      startTime: "12:00",
      endTime: "13:00",
      reasonCode: "LUNCH",
      reasonDetails: "",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      let interval;
      try { interval = zonedRangeToInstants(values, timezoneName); }
      catch (error) { failed(error.message); return; }
      const { startsAt, endsAt } = interval;
      const body = {
        organizationRef,
        scheduleId: schedule.scheduleId,
        startsAt,
        endsAt,
        reasonCode: values.reasonCode === "OTHER" ? values.reasonDetails.trim() || "OTHER" : values.reasonCode,
      };
      await saveBlock(body);
    },
  });

  const saveBlock = async (body, confirmationToken) => {
      if (pending.current) return;
      pending.current = true;
      setBtnLoading(true);
      setSaveError("");
      try {
        const request = { ...body, ...(confirmationToken ? { confirmationToken } : {}) };
        let saved;
        if (isEdit) {
          saved = await updateBlock({ ...request, blockId: block.blockId });
        } else {
          saved = await createBlock(request);
        }
        setConfirmation(null);
        success(isEdit ? "Block updated" : "Block created");
        onHide();
        onSaved?.(saved?.block || { ...body, blockId: saved?.blockId || block?.blockId,
          practitionerRef: schedule.practitionerRef, status: "active" });
      } catch (error) {
        const overlap = blockOverlapFromError(error);
        if (overlap) setConfirmation({ body, overlap });
        else setSaveError(schedulingErrorMessage(error));
      } finally {
        pending.current = false;
        setBtnLoading(false);
      }
  };

  useEffect(() => {
    if (!show) return;
    setSaveError("");
    setConfirmation(null);
    if (block) {
      const knownReason = BLOCK_REASON_CODES.some(reason => reason.value === block.reasonCode);
      formik.setValues({
        ...instantRangeToLocal(block.startsAt, block.endsAt, timezoneName),
        reasonCode: knownReason ? block.reasonCode : "OTHER",
        reasonDetails: knownReason ? "" : block.reasonCode || "",
      });
    } else {
      formik.setValues({
        localDate: initialRange?.localDate || "",
        endDate: initialRange?.endDate || initialRange?.localDate || "",
        startInstant: initialRange?.startInstant,
        endInstant: initialRange?.endInstant,
        startTime: initialRange?.startTime || "12:00",
        endTime: initialRange?.endTime || "13:00",
        reasonCode: "LUNCH",
        reasonDetails: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, block, initialRange]);

  return (
    <Modal show={show} onHide={() => { if (!pending.current) onHide(); }} keyboard={!btnLoading} size="lg" centered backdrop="static" className="custom-dialog scheduling-dialog">
      <Modal.Header closeButton={!btnLoading} className="border-0">
        <Modal.Title>{isEdit ? "Edit blocked time" : "Block time"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <p className="schedule-help">Selected times unavailable for booking new appointments.</p>
          <fieldset disabled={btnLoading || !!confirmation}>
          <DateTimeRangeFields values={formik.values} onChange={formik.setFieldValue} timezoneName={timezoneName} />
          <Row>
            <Col>
              <label htmlFor="calendar-block-reason">Reason</label>
              <select
                id="calendar-block-reason"
                className="form-control"
                value={formik.values.reasonCode}
                onChange={(event) => formik.setFieldValue("reasonCode", event.target.value)}
              >
                {BLOCK_REASON_CODES.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.name}
                  </option>
                ))}
              </select>
              {formik.values.reasonCode === "OTHER" && <>
                <label htmlFor="calendar-block-reason-details">Reason details (optional)</label>
                <input id="calendar-block-reason-details" className="form-control" maxLength={100}
                  value={formik.values.reasonDetails} onChange={event => formik.setFieldValue("reasonDetails", event.target.value)} />
              </>}
            </Col>
          </Row>
          </fieldset>
          {saveError && <p role="alert">{saveError}</p>}
          {confirmation ? <BlockOverlapWarning overlap={confirmation.overlap} isEdit={isEdit} busy={btnLoading}
            onCancel={onHide} onConfirm={() => saveBlock(confirmation.body, confirmation.overlap.confirmationToken)} /> :
          <div className="btn-wrap" style={{ display: "flex", marginTop: "1rem" }}>
            <Button type="button" variant="secondary" disabled={btnLoading} onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>}
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CalendarBlockForm;
